import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button } from '@britishcouncil/react-admin';
import { Input, InputOnChangeData } from 'semantic-ui-react';

type Props = {
  capacity: number;
  min: number;
  max: number;
  editable: boolean;
  onChange: (capacity: number) => void;
};

export const SwaCapacityEditor = ({ capacity, min, max, editable, onChange }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(capacity);

  const error = useMemo(() => {
    if (value > max) {
      return `Value must be less than or equal to ${max}.`;
    }
    if (value < min) {
      return `Value must be greater than or equal to ${min}.`;
    }
    return undefined;
  }, [value, min, max]);

  const startEditing = () => {
    if (!editable) return;
    setValue(capacity);
    setIsEditing(true);
  };

  const commit = () => {
    if (!error) {
      onChange(value);
    }
    setIsEditing(false);
  };

  const cancel = () => {
    setValue(capacity);
    setIsEditing(false);
  };

  const decrement = () => setValue((current) => Math.max(min, current - 1));
  const increment = () => setValue((current) => Math.min(max, current + 1));

  // Keeps focus on the number input when a stepper button is pressed, so clicking +/- doesn't
  // blur the input first and commit (or cancel) the edit before the click itself is handled
  const keepInputFocused = (e: React.MouseEvent) => e.preventDefault();

  if (!isEditing) {
    return (
      <S.Capacity data-testid="room-structure-capacity" editable={editable} onClick={startEditing}>
        {capacity}
      </S.Capacity>
    );
  }

  return (
    <S.Wrapper>
      <Button
        data-testid="decrement-swa-capacity"
        icon="minus"
        compact
        size="mini"
        disabled={value <= min}
        onMouseDown={keepInputFocused}
        onClick={decrement}
      />

      <S.CapacityInput
        autoFocus
        name="swa-capacity"
        type="text"
        inputMode="numeric"
        size="mini"
        value={value}
        onChange={(_e: React.ChangeEvent<HTMLInputElement>, d: InputOnChangeData) => {
          if (d.value === '') {
            return;
          }
          const parsed = Number.parseInt(d.value, 10);
          if (!Number.isNaN(parsed)) {
            setValue(Math.min(max, Math.max(min, parsed)));
          }
        }}
        onBlur={commit}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            commit();
          }
          if (e.key === 'Escape') {
            cancel();
          }
        }}
      />

      <Button
        data-testid="increment-swa-capacity"
        icon="plus"
        compact
        size="mini"
        disabled={value >= max}
        onMouseDown={keepInputFocused}
        onClick={increment}
      />

      {error && (
        <S.Error role="alert" className="ui pointing above prompt label">
          {error}
        </S.Error>
      )}
    </S.Wrapper>
  );
};

const S = {
  Wrapper: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  `,
  Capacity: styled.span<{ editable: boolean }>`
    font-weight: 600;
    white-space: nowrap;
    ${({ editable }) => editable && `cursor: pointer; text-decoration: underline dotted; text-underline-offset: 3px;`}
  `,
  CapacityInput: styled(Input)`
    input {
      width: 40px;
      font-family: inherit;
      font-weight: 600;
    }
  `,
  Error: styled.div`
    &&& {
      margin-top: 40px;
      background-color: #fff;
      background: #fff;
      border: 1px solid #dd4b39;
      color: #b21e1d;
      overflow-wrap: break-word;
      position: fixed;
      white-space: pre-wrap;
    }
  `,
};
